import { QuestionOutlined, UserOutlined } from '@ant-design/icons';
import { User } from '_/contexts/AuthContext';
import { Typography, Avatar } from 'antd';
import Tooltip from 'antd/lib/tooltip';
import React, { Fragment, ReactElement } from 'react';

import classes from './UserProfile.module.css';

interface UserProfileProps {
  fontWeight?: string;
  fontSize?: string;
  onClick?: () => void;
  selectMode?: boolean;
  showDetails?: boolean;
  showUsername?: boolean;
  sidebarMode?: boolean;
  tableMode?: boolean;
  textColor?: string;
  user?: User | null;
}

function setUsername(user: User | undefined, showUsername: boolean): string {
  if (showUsername && (user?.email || user?.username)) {
    return (user?.email || user?.username) as string;
  } else if (showUsername) {
    return 'Unassigned';
  } else {
    return '';
  }
}

const UserProfile = ({
  fontWeight,
  fontSize,
  onClick = () => {},
  selectMode = true,
  showDetails = false,
  showUsername = true,
  sidebarMode = false,
  tableMode = false,
  textColor,
  user = { username: '' },
}: UserProfileProps): ReactElement => {
  const githubId = user?.githubUsername;
  const avatarUrl = githubId ? `https://github.com/${githubId}.png?size=260` : undefined;
  const avatarPreviewUrl = githubId ? `https://github.com/${githubId}.png?size=40` : undefined;
  const userName = setUsername(user || undefined, showUsername);

  const preview = (
    <div
      className={tableMode ? `${classes.container} ${classes.tableMargin}` : classes.container}
      style={{ cursor: selectMode ? 'pointer' : 'auto' }}
      data-test-id="user-container"
      onClick={onClick}
    >
      {avatarPreviewUrl ? (
        <Avatar
          src={avatarPreviewUrl}
          className={tableMode ? classes.tableSmallAvatar : classes.smallAvatar}
          data-test-id="user-avatar-preview"
        />
      ) : (
        <Avatar
          className={tableMode || sidebarMode ? undefined : classes.smallAvatar}
          data-test-id="user-avatar-with-icon-preview"
        >
          <QuestionOutlined />
        </Avatar>
      )}
      <div title={userName} className={classes.usernamePreview} data-test-id="user-full-name-preview">
        <Typography.Text
          style={{
            fontWeight: fontWeight ? fontWeight : undefined,
            color: textColor || undefined,
            fontSize: fontSize || undefined,
          }}
        >
          {userName}
        </Typography.Text>
      </div>
    </div>
  );

  return (
    <Fragment>
      {!showDetails ? (
        preview
      ) : (
        <Tooltip
          classNames={{ root: classes.tooltip }}
          placement="left"
          title={
            <div className={classes.card}>
              <div className={classes.cardItem}>
                {avatarUrl ? (
                  <Avatar src={avatarUrl} size={150} data-test-id="user-avatar" />
                ) : (
                  <Avatar size={150} data-test-id="user-avatar-with-icon">
                    <UserOutlined className={classes.bigIcon} data-test-id="user-avatar-icon" style={{ background: 'black' }} />
                  </Avatar>
                )}
              </div>
              <div title={userName} className={`${classes.cardItem} ${classes.username}`} data-test-id="user-full-name">
                {user?.email ? (
                  <Typography.Link className={classes.link} href={`mailto:${user.email}`} data-test-id="user-email">
                    {userName}
                  </Typography.Link>
                ) : (
                  userName
                )}
              </div>
              {user?.githubId ? (
                <div className={`${classes.cardItem} ${classes.noWrap}`}>
                  <Typography.Link
                    className={classes.link}
                    href={`https://github.com/${githubId}`}
                    target="_blank"
                    rel="noopener"
                    data-test-id="user-home-page"
                  >
                    {`https://github.com/${githubId}`}
                  </Typography.Link>
                </div>
              ) : null}
            </div>
          }
        >
          {preview}
        </Tooltip>
      )}
    </Fragment>
  );
};

export default UserProfile;
